import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, LogIn, UserPlus, ArrowLeft } from 'lucide-react';
import Loader from '../ui/Loader';
import SitewideMessage from '../common/SitewideMessage';

const AuthPage = () => {
    const [searchParams] = useSearchParams();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const navigate = useNavigate();
    const returnUrlRef = useRef('/my-account');

    const sanitizeReturnUrl = (rawUrl) => {
        if (!rawUrl) return '/my-account';

        try {
            const decoded = decodeURIComponent(rawUrl);
            if (!decoded) return '/my-account';

            if (decoded.startsWith('http')) {
                try {
                    const parsed = new URL(decoded);
                    if (parsed.origin === window.location.origin) {
                        return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/my-account';
                    }
                } catch (_) {
                    return '/my-account';
                }
                return '/my-account';
            }

            if (!decoded.startsWith('/')) {
                return `/${decoded}`;
            }

            return decoded;
        } catch (_) {
            return '/my-account';
        }
    };

    useEffect(() => {
        const modeParam = searchParams.get('mode');
        const emailParam = searchParams.get('email');
        const returnParam = searchParams.get('return');
        
        // Also check location.state for returnTo (React Router state)
        const locationState = window.history?.state?.usr || {};
        const stateReturnTo = locationState?.returnTo;

        if (modeParam === 'signup') {
            setIsLogin(false);
        } else if (modeParam === 'login') {
            setIsLogin(true);
        }

        if (emailParam) {
            setEmail(emailParam);
        }

        // Prefer URL param, fallback to state
        const returnUrl = returnParam || stateReturnTo;
        const sanitizedReturn = sanitizeReturnUrl(returnUrl);
        returnUrlRef.current = sanitizedReturn;
    }, [searchParams]);

    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session) {
                // Small delay to ensure state is updated
                setTimeout(() => {
                    const returnUrl = returnUrlRef.current || '/my-account';
                    // Only navigate if we're still on the login page
                    if (window.location.pathname === '/login' || window.location.pathname === '/') {
                        navigate(returnUrl, { replace: true });
                    }
                }, 100);
            }
        });
        
        return () => {
            if (subscription && typeof subscription.unsubscribe === 'function') {
                subscription.unsubscribe();
            }
        };
    }, [navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (response.status === 404) {
                const { error } = await supabase.auth.signInWithPassword({ email, password });
                if (error) {
                    throw error;
                }
                setMessage('Logged in successfully!');
                navigate(returnUrlRef.current || '/my-account', { replace: true });
                return;
            }

            const result = await response.json();

            if (!response.ok) {
                if (response.status === 429) {
                    const retrySeconds = result?.retry_after_seconds ?? 60;
                    setError(`Too many attempts. Please wait ${retrySeconds} seconds before trying again.`);
                } else {
                    setError(result?.error || 'Unable to sign in with those credentials.');
                }
                return;
            }

            const session = result?.data?.session;
            if (session?.access_token && session?.refresh_token) {
                const { error: setSessionError } = await supabase.auth.setSession({
                    access_token: session.access_token,
                    refresh_token: session.refresh_token
                });

                if (setSessionError) {
                    throw setSessionError;
                }
            }

            setMessage('Logged in successfully!');
            navigate(returnUrlRef.current || '/my-account', { replace: true });
        } catch (error) {
            try {
                const { error: fallbackError } = await supabase.auth.signInWithPassword({ email, password });
                if (fallbackError) {
                    throw fallbackError;
                }
                setMessage('Logged in successfully!');
                navigate(returnUrlRef.current || '/my-account', { replace: true });
            } catch (innerError) {
                setError(innerError.message || error.message || 'Unable to sign in. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');
        try {
            const { data, error } = await supabase.auth.signUp({ email, password });

            if (error) {
                if (error.message?.toLowerCase().includes('already registered')) {
                    setError('An account with this email already exists. Please sign in instead.');
                } else {
                    setError(error.message);
                }
                return;
            }

            if (data?.user?.identities && data.user.identities.length === 0) {
                setError('An account with this email already exists. Try signing in instead.');
                return;
            }

            if (data.user) {
                // Note: user_profiles will be created by database trigger
                // No need to manually insert here
            }
            setMessage('Registration successful! Please check your email to confirm your account.');
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setError('Please enter your email address.');
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) throw error;

            setMessage('Password reset email sent! Please check your inbox for further instructions.');
            setShowForgotPassword(false);
        } catch (error) {
            setError(error.message || 'Failed to send password reset email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Kapitalized</h1>
                    <p className="text-gray-600 mb-8">Your business management platform</p>
                </div>
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    {isLogin ? 'Sign in to your account' : 'Create a new account'}
                </h2>
            </div>
            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                {/* Zone 3 - Login page messages */}
                <div className="mb-4">
                    <SitewideMessage zone="zone3" userRoles={[]} />
                </div>
                <div className="bg-white py-8 px-4 shadow rounded-lg sm:px-10">
                    {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded" role="alert"><p>{error}</p></div>}
                    {message && <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded" role="alert"><p>{message}</p></div>}
                    
                    {showForgotPassword ? (
                        <form className="space-y-6" onSubmit={handleForgotPassword}>
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Password</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                    Enter your email address and we'll send you a link to reset your password.
                                </p>
                            </div>
                            <div>
                                <label htmlFor="reset-email" className="sr-only">
                                    Email address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="reset-email"
                                        name="reset-email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Email address"
                                    />
                                </div>
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForgotPassword(false);
                                        setError('');
                                        setMessage('');
                                    }}
                                    className="flex-1 flex items-center justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {loading ? <Loader /> : 'Send Reset Link'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <form className="space-y-6" onSubmit={isLogin ? handleLogin : handleSignup}>
                            <div>
                                <label htmlFor="email" className="sr-only">
                                    Email address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Email address"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="password" className="sr-only">
                                    Password
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required={!isLogin}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="Password"
                                    />
                                </div>
                                {isLogin && (
                                    <div className="mt-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowForgotPassword(true);
                                                setError('');
                                                setMessage('');
                                            }}
                                            className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                                        >
                                            Forgot password?
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                >
                                    {loading ? <Loader /> : isLogin ? (
                                        <>
                                            <LogIn className="h-5 w-5 mr-2" />
                                            Sign In
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="h-5 w-5 mr-2" />
                                            Sign Up
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">
                                    Or
                                </span>
                            </div>
                        </div>
                        <div className="mt-6 text-center">
                            <button
                                onClick={() => setIsLogin(!isLogin)}
                                className="font-medium text-blue-600 hover:text-blue-500"
                            >
                                {isLogin ? 'Create a new account' : 'Already have an account? Sign in'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuthPage;